import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { createPanelLoader } from "@/panel/panelLoader";

/**
 * Ortak giriş + OTP modal akışı (LoginPage3 / LoginPage4)
 */
export function useLoginOtpFlow({ login, finalizeOtpLogin, isDark }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberChecked, setRememberChecked] = useState(false);
  const [rememberDays, setRememberDays] = useState(0);
  const [deviceLabel, setDeviceLabel] = useState("");
  const [rememberModalOpen, setRememberModalOpen] = useState(false);
  const [otpFlow, setOtpFlow] = useState(null);
  const loginLoader = useMemo(() => createPanelLoader({ isDark }), [isDark]);

  useEffect(() => {
    loginLoader.setTheme(isDark);
  }, [isDark, loginLoader]);

  const handleRememberCheckedChange = useCallback((checked) => {
    setRememberChecked(checked);
    if (!checked) {
      setRememberDays(0);
      setDeviceLabel("");
    }
  }, []);

  const runLogin = useCallback(async (days, label) => {
    setLoading(true);
    let nextOtpFlow = null;
    loginLoader.show({ message: "Lütfen bekleyiniz", fullscreen: true });
    try {
      const rememberOpts = days > 0 ? { rememberDays: days, deviceLabel: label } : {};
      const result = await login(username, password, rememberOpts);
      if (result.completed) {
        toast.success("Giriş başarılı");
        setOtpFlow(null);
        return;
      }
      if (result.resumed) {
        toast.info(result.message || "Doğrulama kodunuz hâlâ geçerli, kaldığınız yerden devam edin.");
      }
      nextOtpFlow = {
        creds: { username, password },
        chooseChannel: result.choose_channel === true,
        channels: result.channels,
        phoneMasked: result.phone_masked,
        otpState: result.otp_required ? result : null,
        rememberDays: days,
        deviceLabel: label,
      };
    } catch (error) {
      const data = error.response?.data;
      if (data?.blocked) {
        nextOtpFlow = {
          creds: { username, password },
          chooseChannel: false,
          blocked: true,
          retryIn: data.retry_in || 900,
          rememberDays: days,
          deviceLabel: label,
        };
      } else if (data?.missing_gsm || data?.otp_setup_required) {
        toast.error(data?.detail || "GSM numaranız tanımlı değil. Lütfen yöneticiniz ile görüşünüz.", {
          duration: 10000,
        });
      } else {
        toast.error(data?.detail || "Giriş başarısız");
      }
    } finally {
      setLoading(false);
      if (nextOtpFlow) {
        setOtpFlow(nextOtpFlow);
      } else {
        loginLoader.hide();
      }
    }
  }, [username, password, login, loginLoader]);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("E-posta/kullanıcı adı ve şifre gerekli");
      return;
    }
    if (rememberChecked && rememberDays <= 0) {
      setRememberModalOpen(true);
      return;
    }
    await runLogin(rememberChecked ? rememberDays : 0, deviceLabel);
  }, [username, password, rememberChecked, rememberDays, deviceLabel, runLogin]);

  const handleRememberConfirm = useCallback((days, label) => {
    setRememberDays(days);
    setDeviceLabel(label);
    setRememberModalOpen(false);
    runLogin(days, label);
  }, [runLogin]);

  const handleRememberCancel = useCallback(() => {
    setRememberModalOpen(false);
  }, []);

  const handleOtpComplete = useCallback((data) => {
    finalizeOtpLogin(data);
    setOtpFlow(null);
    toast.success("Giriş başarılı");
  }, [finalizeOtpLogin]);

  const dismissOtpFlow = useCallback(() => setOtpFlow(null), []);

  return {
    username,
    setUsername,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    loading,
    rememberChecked,
    rememberDays,
    rememberModalOpen,
    handleRememberCheckedChange,
    handleRememberConfirm,
    handleRememberCancel,
    otpFlow,
    loginLoader,
    handleLogin,
    handleOtpComplete,
    dismissOtpFlow,
  };
}
